<script lang="ts">
    import type {FormControlOptions} from '../../internal/forms/types';
    import type {ValueEditorSize} from '../../types';
    import KpDropdownListbox from '../../../ui-components/dropdown-listbox/KpDropdownListbox.svelte';
    import {getContext, onDestroy} from 'svelte';
    import type {SingleAcceptableValueEditorLocalizations, SingleAcceptableValueEditorOptions} from './types';
    import type {ValueEditorValidations} from '../../kp-value-editor/types';
    import {EDITORS_LOCALIZE} from '../../context-keys';
    import type {FormControl} from '../../internal/forms/form-control';
    import { FormControlBuilder} from '../../internal/forms/form-control';
    import type {EditorsLocalizationFunction} from '../../localizations/types';
    import {createInputDescription} from '../../description/utils';
    import {RadioGroup, RadioGroupOption} from '@rgossiaux/svelte-headlessui';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import {
        arrayToScalarValueFormatter,
        getOptionById,
        getOptionId,
        getOptionIdResolver,
        modelToIdentifierFormatter,
        scalarValueToArrayParser,
        identifierToModelParser,
        validatorFactory, tryToFindOptionById
    } from './utils';
    import {touched} from '../../internal/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {focusOnMount} from '../../internal/use.focus-on-mount';
    import {style} from 'svelte-forms';
    import {addAttributes} from '../../internal/use.add-attributes';
    import DescriptionIdProvider from '../../description/DescriptionIdProvider.svelte';
    import LabelIdProvider from '../../label/LabelIdProvider.svelte';
    import DropdownListboxLabelProvider
        from '../../../ui-components/dropdown-listbox/DropdownListboxLabelProvider.svelte';
    import {isFunction, isNullOrUndefined, isUndefined} from '../../../../shared/utils/custom-utils';
    import NullableOptionLabel from './NullableOptionLabel.svelte';
    import type {HTMLActionEntry} from 'node_modules/@rgossiaux/svelte-headlessui/hooks/use-actions';
    import {getLogger} from '../../../../svelte/context';

    type MODEL_VALUE = $$Generic;

    export let model: MODEL_VALUE | MODEL_VALUE[];
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: SingleAcceptableValueEditorOptions<MODEL_VALUE>;
    export let validations: ValueEditorValidations;


    model = normalizeModelIfNeeded(model);

    const localize: EditorsLocalizationFunction<SingleAcceptableValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);

    onDestroy(() => {
        subscription.unsubscribe();
    });

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withOptions(formControlOptions)
        .withValidators([...validatorFactory(validations)])
        .withParsers([identifierToModelParser(() => options.acceptableValues, options), ...options.modelAsArray ? [scalarValueToArrayParser()] : []])
        .withFormatters([modelToIdentifierFormatter(options), ...options.modelAsArray ? [arrayToScalarValueFormatter()] : []])
        .build(getLogger());

    $: model = normalizeModelIfNeeded(model);

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    const viewValue$ = formControl.getViewValue$();

    let inlineMode: boolean;
    $: inlineMode = options.switchToInlineModeThreshold !== 0 && options.acceptableValues.length >= options.switchToInlineModeThreshold;

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    function normalizeModelIfNeeded(modelValue: MODEL_VALUE | MODEL_VALUE[]): MODEL_VALUE | MODEL_VALUE[] {
        if (!options.modelAsArray) {
            return modelValue;
        }

        if (Array.isArray(modelValue)) {
            if (modelValue.length > 1) { // [a, b, ...]
                return modelValue.slice(0, 1); // [a]
            }
            if (modelValue.length < 1) { // []
                return null;
            }
            return modelValue;
        } else {
            if (modelValue === null) {
                return null;
            } else {
                return [modelValue]
            }
        }
    }

    function selectOption(optionIdentifier: string | number): void {
        formControl.setViewValue(optionIdentifier);
    }

    $: allowNullValue = options.allowSelectNull && !validations.required

    let selectableOptionsIdentifiers: (string | number)[];
    $: selectableOptionsIdentifiers = createSelectableOptions(options.acceptableValues, allowNullValue, options.sortComparator);

    function createSelectableOptions(acceptableValues: typeof options.acceptableValues,
                                     addNullOption: boolean,
                                     comparator? :typeof options.sortComparator): (string | number)[] {
        const acceptableValuesCopy = [...acceptableValues];
        if (isFunction(comparator)) {
            try {
                acceptableValuesCopy.sort((element1, element2) => comparator({element1, element2}));
            } catch (e) {
                throw new Error(`Error in custom sortComparator: ${e}`);
            }
        }
        const selectableOptionsIds = acceptableValuesCopy.map((option) => getOptionId(option, getOptionIdResolver(options)));
        return addNullOption ? [...selectableOptionsIds, null] : selectableOptionsIds;
    }

    function isOptionDisabled(optionIdentifier: string | number): boolean {
        if (isDisabled) {
            return true;
        } else {
            if (!isFunction(options.optionDisabledResolver)) {
                throw new Error(`disabledItemResolver is not function: ${options.optionDisabledResolver}`);
            }

            const option = getOptionById(options.acceptableValues, getOptionIdResolver(options), optionIdentifier);
            try {
                return options.optionDisabledResolver({options, item: option, model});
            } catch (e) {
                throw new Error(`Error in custom optionDisabledResolver: ${e}`);
            }
        }
    }

    function addModelValueIfNotInOptions(acceptableValues: MODEL_VALUE[]): MODEL_VALUE[] {
        const optionIdResolver = getOptionIdResolver(options);
        const modelValue = options.modelAsArray ? model[0] : model; // model can be scalar or array of single element
        const option = tryToFindOptionById(acceptableValues, optionIdResolver, getOptionId(modelValue, optionIdResolver));

        if (isUndefined(option)) {
            return [...acceptableValues, modelValue];
        }

        return acceptableValues;
    }

    const addAttributesOptions = new Map<string, any>([
        ['name', editorName],
        ['aria-required', validations.required]
    ]);

    const blockModeTouchedAction: HTMLActionEntry = [touched, {formControl, touchTriggerEvent: 'focusout'}]; // svelte-check forced me to explicitly type this

    export function getFormController(): FormControl<typeof model> {
        return formControl;
    }
</script>

<template>
    {#if inlineMode}
        <KpDropdownListbox value={$viewValue$}
                           id={editorId}
                           {size}
                           options={selectableOptionsIdentifiers}
                           additionalButtonClass="form-control btn-default single-acceptable-inline-button form-control-button"
                           disabled={isDisabled || selectableOptionsIdentifiers.length === 1}
                           {isOptionDisabled}
                           use={[
                               [addAttributes, addAttributesOptions],
                               [touched, {formControl}],
                               [focusOnMount, isFocused],
                               [ariaInvalid, formControl],
                               [ariaErrormessage, formControl],
                               [ariaDescribedby, inputDescription],
                               [style, {field: formControl.getFieldStateAsStore()}]]}
                           data-main-input
                           on:change={(event) => selectOption(event.detail)}>
            <svelte:fragment slot="label">
                <DropdownListboxLabelProvider/>
            </svelte:fragment>
            <svelte:fragment slot="button">
                <span class="input-button-label">
                    {#if isNullOrUndefined($viewValue$) && (!allowNullValue || !!placeholder)}
                        <span class="placeholder">{placeholder}</span>
                    {:else}
                        <NullableOptionLabel optionIdentifier={$viewValue$} let:optionIdentifier>
                            <svelte:component this={options.optionLabelComponent}
                                              option={getOptionById(addModelValueIfNotInOptions(options.acceptableValues), getOptionIdResolver(options), optionIdentifier)}>
                            </svelte:component>
                        </NullableOptionLabel>
                    {/if}
                </span>
                <span aria-hidden="true" class="caret"></span>
            </svelte:fragment>
            <span class="option-label" slot="option" let:option>
                <NullableOptionLabel optionIdentifier={option} let:optionIdentifier>
                    <svelte:component this={options.optionLabelComponent}
                                      option={getOptionById(options.acceptableValues, getOptionIdResolver(options), optionIdentifier)}>
                    </svelte:component>
                </NullableOptionLabel>
            </span>
        </KpDropdownListbox>

        <ErrorMessages formController={formControl} {forceShowErrors} {size} customClass="single-acceptable-select-error"/>
    {:else}
        <RadioGroup data-id={editorId}
                    data-name={editorName}
                    class="list-group size-{size}"
                    value={$viewValue$}
                    disabled={isDisabled}
                    aria-required={validations.required}
                    use={[
                        ...isDisabled ? [] : [blockModeTouchedAction],
                        [focusOnMount, isFocused],
                        [ariaInvalid, formControl],
                        [ariaErrormessage, formControl],
                        [style, {field: formControl.getFieldStateAsStore()}]]}
                    on:change={(event) => selectOption(event.detail)}
                    data-main-input>

            <LabelIdProvider/>

            {#each selectableOptionsIdentifiers as selectableOptionIdentifier (selectableOptionIdentifier)}
                <RadioGroupOption as="button"
                                  type="button"
                                  disabled={isOptionDisabled(selectableOptionIdentifier)}
                                  class={`list-group-item ${
                                      $viewValue$ === selectableOptionIdentifier ? 'active' : ''} ${
                                      isOptionDisabled(selectableOptionIdentifier) ? 'disabled' : ''}`}
                                  value={selectableOptionIdentifier}>
                    <span class="option-label">
                        <NullableOptionLabel optionIdentifier={selectableOptionIdentifier} let:optionIdentifier>
                            <svelte:component this={options.optionLabelComponent}
                                              option={getOptionById(options.acceptableValues, getOptionIdResolver(options), optionIdentifier)}>
                             </svelte:component>
                        </NullableOptionLabel>
                    </span>
                </RadioGroupOption>
            {/each}

            <DescriptionIdProvider {inputDescription}/>
        </RadioGroup>

        <ErrorMessages formController={formControl} {forceShowErrors} {size}/>
    {/if}

    <Description {inputDescription}/>
</template>

<style lang="less" global>
    @import (reference) "bootstrap-less/bootstrap/variables";
    @disabled-opacity: 0.5;

    kp-value-editor[data-type="single-acceptable"] {

        button.list-group-item.active.disabled {
            opacity: @disabled-opacity;
        }

        // used patch version of bootstrap does not highlight buttons in list group
        // code copied from newer version, but due to the selector specificity
        // it overrides styles of .active state, so apply style only if item is not .active or .disabled
        button.list-group-item:not(.active):not(.disabled) {
            color: @list-group-link-color;

            // Hover state
            &:hover,
            &:focus {
                color: @list-group-link-hover-color;
                text-decoration: none;
                background-color: @list-group-hover-bg;
            }
        }

        button.list-group-item {
            width: 100%;
        }

        .list-group {
            margin-bottom: 0;

            &.size-md {
                .list-group-item {
                    padding-top: 6px;
                    padding-bottom: 6px;
                }
            }

            &.size-sm,
            &.size-xs {
                .list-group-item {
                    font-size: 12px;
                    line-height: 1.5;
                }
            }

            &.size-sm {
                .list-group-item {
                    padding-top: 6px;
                    padding-bottom: 4px;
                }
            }

            &.size-xs {
                .list-group-item {
                    padding-top: 2px;
                    padding-bottom: 0;
                }
            }
        }

        .input-button-label {
            margin-right: 12px; // to ensure gap between label and caret
        }

        .caret {
            position: absolute;
            height: 10px;
            top: 50%;
            right: 10px;
            margin-top: -2px;
        }

        .input-xs {
            .caret {
                right: 5px;
            }
        }

        .error-message.single-acceptable-select-error {
            margin-top: 1px;
        }

        .single-acceptable-inline-button {
            text-align: left;
            width: 100%; // to override .form-control `width: auto` in media queries
        }
    }

    .has-error {
        kp-value-editor[data-type="single-acceptable"] {
            .list-group .list-group-item {
                border-left-color: @state-danger-text;
                border-right-color: @state-danger-text;
                transition: border-color 0.15s ease-in-out;
                &:first-child {
                    border-top-color: @state-danger-text;
                }
                &:last-child {
                    border-bottom-color: @state-danger-text;
                }
            }
        }

        /* reset error styles if editor is nested in meta-editor which has error */
        .has-not-error {
            kp-value-editor[data-type="single-acceptable"] {
                .list-group .list-group-item {
                    border-color: @list-group-border;
                }
            }
        }
    }
</style>
